/* Local set supplements — cards that exist before the upstream dump has them.
 *
 * The card database is fetched from punk-records, which is generated from
 * Bandai's published list. That means a set is unavailable here until Bandai
 * posts it and punk-records regenerates: on the day OP-17 was announced the
 * upstream index still ended at OP-16, in every language.
 *
 * A local set file fills that window. It is merged UNDER the upstream data —
 * see `mergeSets` — so the handover is automatic: the moment punk-records ships
 * the set, its entries win and the local file becomes inert without anyone
 * having to remember to delete it.
 *
 * The dangerous failure this file exists to prevent is a half-known card
 * reaching the engine. `withStubs` in cards.js already documents what happened
 * when unknown ids were stubbed as vanilla 4-cost 5000 bodies: legality was
 * then checked against that fiction. A pre-release card with a name but no
 * printed cost, power or effect is the same hazard wearing a real name, so
 * those are marked `provisional` and kept out of decks until the numbers land.
 */

/* Fields the engine and the deck rules genuinely cannot work without. A card
 * missing any of them cannot be played, simulated, or checked for colour
 * legality, so it is provisional no matter how much else we know about it.
 *
 * Power is not on this list: a null power means printed zero, not unknown.
 * `missingFields` explains why. */
export const PLAYABLE_FIELDS = ['category', 'colors', 'cost'];

export const CATEGORIES = ['Leader', 'Character', 'Event', 'Stage'];
const COLORS = ['Red', 'Green', 'Blue', 'Purple', 'Black', 'Yellow'];

const isNum = v => typeof v === 'number' && Number.isFinite(v);

/**
 * Is this card being held back as not-yet-printed?
 *
 * Deliberately a stored flag rather than a live re-derivation of "does this
 * card look complete", because plenty of perfectly real cards don't:
 *
 *   - Events with no printed cost, paid for by an alternative cost in their
 *     text ("DON!! −1:", "You may trash 1 card:"). The whole Enel Event suite
 *     is this shape — OP15-074 Varie, OP15-076 Lightning Beast Kiten and the
 *     rest — and a field-sniffing check condemned all of them.
 *   - Events and Stages with no power box at all.
 *   - Characters printed with *zero* power. Bandai prints "—" in the power box
 *     rather than "0", so the upstream dump stores null — see `missingFields`.
 *
 * So the flag is computed once, at import, by `computeProvisional`, and only
 * ever set on cards that came out of a local set file. An upstream card is
 * never provisional: upstream is by definition the printed article.
 */
export function isProvisional(card) {
  return card?.provisional === true;
}

/**
 * Which required fields a local set entry is still missing.
 *
 * Mirrors what the rules actually need, not a uniform "every field present":
 * cost is not required for an Event, and power is only a thing for Leaders and
 * Characters.
 *
 * Power is *not* checked, and that is deliberate. Bandai prints "—" rather than
 * "0" in the power box of a zero-power Character, so the upstream dump — which
 * is generated from their list — stores `null` for every one of them. There are
 * 149 in the current pool and not a single literal `0`, which is what settles
 * it: `null` is this project's encoding for printed zero, not for unknown.
 *
 * Otama, Makino, Rika and the rest of the 1-cost 2000-counter support bodies
 * are exactly that shape. Treating their power as missing marked them
 * provisional, which means `addCard` and `validate` both refuse them — real,
 * legal, commonly-played cards, undeckable.
 *
 * The same regression as the costless Events above, one field over, and the
 * check bought nothing: a genuine placeholder has no category, colours or cost
 * either, so it is still caught three times over. `data/sets/OP17.json` was
 * 119 such entries and every one stayed provisional without this, right up
 * until upstream published the set and the file was retired.
 *
 * The cost of getting it wrong is asymmetric. A card wrongly held back is
 * invisible and unplayable; a Character whose power is genuinely unknown
 * simulates as a 0-power body, which understates it — and understating is the
 * direction this project treats as safe.
 */
export function missingFields(card) {
  if (!card) return [...PLAYABLE_FIELDS];
  const out = [];
  if (!CATEGORIES.includes(card.category)) out.push('category');
  if (!Array.isArray(card.colors) || card.colors.length === 0) out.push('colors');
  // An Event's cost can legitimately be absent; everything else needs one.
  if (card.category !== 'Event' && !isNum(card.cost)) out.push('cost');
  return out;
}

/** Does this local entry still lack something the rules need? */
export function computeProvisional(card) {
  return missingFields(card).length > 0;
}

const ID_RE = /^[A-Z]{1,4}\d{2}-\d{3}$/;

/**
 * Validate and normalise one raw entry from a set file.
 *
 * Throws rather than coercing. A set file is authored or generated by us, so a
 * bad field is a bug to fix at the source — silently defaulting it is how a
 * wrong cost reaches a win-rate table and is never questioned again.
 *
 * @returns {object} a card record shaped like the upstream dump's
 */
export function normalizeCard(raw, { set } = {}) {
  if (!raw || typeof raw !== 'object') throw new TypeError('card entry is not an object');

  const id = String(raw.id || '').toUpperCase();
  if (!ID_RE.test(id)) throw new TypeError(`bad card id: ${JSON.stringify(raw.id)}`);
  if (set && !id.startsWith(`${set}-`)) throw new TypeError(`${id} does not belong to set ${set}`);

  const name = String(raw.name ?? '').trim();
  if (!name) throw new TypeError(`${id}: missing name`);

  if (raw.category != null && !CATEGORIES.includes(raw.category)) {
    throw new TypeError(`${id}: unknown category ${JSON.stringify(raw.category)}`);
  }
  const colors = raw.colors == null ? [] : raw.colors;
  if (!Array.isArray(colors)) throw new TypeError(`${id}: colors must be an array`);
  for (const c of colors) {
    if (!COLORS.includes(c)) throw new TypeError(`${id}: unknown colour ${JSON.stringify(c)}`);
  }
  for (const k of ['cost', 'power', 'counter']) {
    if (raw[k] != null && !isNum(raw[k])) throw new TypeError(`${id}: ${k} must be a number or null`);
  }

  const card = {
    id,
    name,
    category: raw.category ?? null,
    colors,
    cost: raw.cost ?? null,
    power: raw.power ?? null,
    counter: raw.counter ?? null,
    effect: raw.effect ?? null,
    trigger: raw.trigger ?? null,
    types: Array.isArray(raw.types) ? raw.types : [],
    attributes: Array.isArray(raw.attributes) ? raw.attributes : [],
    rarity: raw.rarity ?? null,
    /* Provenance travels with the card. When a number here turns out to be
     * wrong, the first question is always "where did this come from". */
    source: raw.source ?? null,
  };
  if (raw.img_full_url) card.img_full_url = raw.img_full_url;
  /* Community rating and commentary. Not rules data and never read by the
   * engine — it is opinion, and it is labelled as opinion. */
  if (raw.review) card.review = raw.review;

  /* An explicit `provisional: true` in the source file wins, which is how a
   * card with complete-looking but unconfirmed stats can be held back. */
  if (raw.provisional === true || computeProvisional(card)) {
    card.provisional = true;
    card.missing = missingFields(card);
  }
  return card;
}

/**
 * Validate a whole set file.
 * @returns {{set: string, source: string|null, generated: string|null, cards: object[]}}
 */
export function normalizeSetFile(json) {
  if (!json || typeof json !== 'object') throw new TypeError('set file is not an object');
  const set = String(json.set || '').toUpperCase();
  if (!/^[A-Z]{1,4}\d{2}$/.test(set)) throw new TypeError(`bad set code: ${JSON.stringify(json.set)}`);
  if (!Array.isArray(json.cards)) throw new TypeError(`${set}: cards must be an array`);

  const cards = [];
  const seen = new Set();
  for (const raw of json.cards) {
    const c = normalizeCard(raw, { set });
    if (seen.has(c.id)) throw new TypeError(`${set}: duplicate card ${c.id}`);
    seen.add(c.id);
    cards.push(c);
  }
  return {
    set,
    source: json.source ?? null,
    generated: json.generated ?? null,
    note: json.note ?? null,
    cards,
  };
}

/**
 * Merge normalised set files into a card database, in place.
 *
 * Upstream wins. A local entry is only inserted where the id is absent, which
 * makes the handover automatic and means a stale local file can never override
 * the real printed card. Returns what actually happened, because a merge that
 * silently did nothing is worth knowing about.
 *
 * @param {Record<string,object>} db
 * @param {Array<{set:string, cards:object[]}>} sets
 */
export function mergeSets(db, sets) {
  const added = [];
  const superseded = [];
  for (const s of sets || []) {
    for (const c of s.cards) {
      if (db[c.id]) { superseded.push(c.id); continue; }
      db[c.id] = c;
      added.push(c.id);
    }
  }
  return { added, superseded };
}
